import React, {useEffect, useState, useLayoutEffect} from 'react';
import styled from 'styled-components';
import {Link, useLocation} from 'react-router-dom';

import MenuBookIcon from '@mui/icons-material/MenuBook';
import FaceIcon from '@mui/icons-material/Face';

import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CategoryIcon from '@mui/icons-material/Category';
import LabelImportantIcon from '@mui/icons-material/LabelImportant';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import {$get} from '../lib/requests';

import {useShell} from './Shell';
import {qurl} from '../lib/urlgen';

const Container = styled.ul<{open?: boolean}>`
  width: 100%;
  height: calc(100vh - 130px);
  overflow-y: auto;
  margin: 0;
  padding-top: 8px;
  padding-left: 0;
  padding-right: 8px;

  a {
    text-decoration: none;
    color: rgb(32, 33, 36);

    &:hover,
    &:focus,
    &:active {
      color: rgb(32, 33, 36);
    }
  }
`;

const TopContainer = styled(Container)<{open?: boolean}>`
  height: 64px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
`;

const Li = styled.li<{open?: boolean; active?: boolean}>`
  position: relative;
  list-style-type: none;
  height: 36px;
  width: 100%;

  display: flex;
  align-items: center;

  padding-left: ${props => (props.open ? '26px' : '20px')};
  padding-right: ${props => (props.open ? '12px' : '12px')};

  border-radius: 0 18px 18px 0;
  cursor: pointer;

  font-weight: ${props => (props.active ? '600' : 'normal')};
  background: ${props =>
    props.active && props.open ? '#d3e3fd' : 'transparent'};

  &:hover {
    ${props =>
      props.open && !props.active ? 'background: rgba(32, 33, 36, 0.06);' : ''}
  }
`;

const NewBookContainer = styled.div`
  margin-top: 1px;
  height: 64px;
  border-bottom: 1px rgba(32, 33, 36, 0.1) solid;
  width: 100%;
`;

const IconContainer = styled.div<{open?: boolean; active?: boolean}>`
  height: ${props => (props.open ? '20px' : '32px')};
  width: ${props => (props.open ? '20px' : '32px')};
  margin-right: ${props => (props.open ? '12px' : '0')};
  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: ${props => (!props.open ? '16px' : '0')};
  background: ${props =>
    !props.open && props.active ? '#d3e3fd' : 'transparent'};

  &:hover {
    ${props =>
      !(props.open || props.active)
        ? 'background: rgba(32, 33, 36, 0.06);'
        : ''}
  }
`;

const LabelContainer = styled.div``;

const MetaContainer = styled.div`
  margin-left: auto;
`;

const ExpandIconContainer = styled.div`
  position: absolute;
  top: 9px;
  left: 6px;
`;

function Line({
  icon = <LabelImportantIcon fontSize="small" />,
  href = '',
  label,
  meta,
  onClick = () => {},
  expandable = false,
  expanded = false,
}: {
  icon?: React.ReactNode;
  href?: string;
  label: React.ReactNode;
  meta?: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  expandable?: boolean;
  expanded?: boolean;
}) {
  const {pathname, search} = useLocation();
  const active = window.encodeURI(href) === pathname + search;
  const {getState} = useShell();
  const {isLeftSidebarOpen: isOpen} = getState();

  return (
    <Link to={href} onClick={onClick}>
      <Li active={active} open={isOpen}>
        {isOpen && expandable && (
          <ExpandIconContainer>
            {expanded ? (
              <ArrowDropDownIcon fontSize="small" />
            ) : (
              <ArrowRightIcon fontSize="small" />
            )}
          </ExpandIconContainer>
        )}
        <IconContainer open={isOpen} active={active}>
          {icon}
        </IconContainer>
        {isOpen && (
          <>
            <LabelContainer>{label}</LabelContainer>
            {meta && <MetaContainer>{meta}</MetaContainer>}
          </>
        )}
      </Li>
    </Link>
  );
}

interface SidebarData {
  numBooks?: number;
  numCategories?: number;
  numAuthors?: number;
  numYears?: number;
  topAuthors?: {name: string; bookCount: number}[];
  topCategories?: {name: string; bookCount: number}[];
  topYears?: {year: number; bookCount: number}[];
}

export default function LeftSidebar({open = true}: {open?: boolean}) {
  const [data, setData] = useState<SidebarData>({});
  const [authorsExpanded, setAuthorsExpanded] = useState(false);
  const [categoriesExpanded, setCategoriesExpanded] = useState(true);
  const [yearsExpanded, setYearsExpanded] = useState(false);

  const location = useLocation();
  useLayoutEffect(() => {
    const {pathname, search} = location;
    const url = pathname + search;

    const isAuthorPage = data.topAuthors
      ? Boolean(
          data.topAuthors.find(
            ({name}: {name: string}) =>
              window.encodeURI(qurl('ଲେଖକ', name)) === url
          )
        )
      : window.encodeURI(qurl('ଲେଖକ', '')) === url;
    const isCategoryPage = data.topCategories
      ? Boolean(
          data.topCategories.find(
            ({name}: {name: string}) =>
              window.encodeURI(qurl('ବିଷୟ', name)) === url
          )
        )
      : window.encodeURI(qurl('ବିଷୟ', '')) === url;
    const isYearPage = data.topYears
      ? Boolean(
          data.topYears.find(
            ({year}: {year: number}) =>
              window.encodeURI(qurl('ବର୍ଷ', year)) === url
          )
        )
      : window.encodeURI(qurl('ବର୍ଷ', '')) === url;

    if (isAuthorPage) setAuthorsExpanded(true);
    if (isCategoryPage) setCategoriesExpanded(true);
    if (isYearPage) setYearsExpanded(true);
  }, [location, data]);

  const {getState, setLeftSidebarOpen} = useShell();
  const {isLeftSidebarOpen: isOpen} = getState();

  function toggleAuthors(e: React.MouseEvent) {
    e.preventDefault();
    setAuthorsExpanded(x => !x);
  }

  function toggleCategories(e: React.MouseEvent) {
    e.preventDefault();
    setCategoriesExpanded(x => !x);
  }

  function toggleYears(e: React.MouseEvent) {
    e.preventDefault();
    setYearsExpanded(x => !x);
  }

  useEffect(() => {
    setLeftSidebarOpen(open);
    $get<SidebarData>('/api/sidebar')
      .then(x => {
        if (x) setData(x);
      })
      .catch(err => {
        console.error('Error fetching sidebar data:', err);
      });
  }, [open, setLeftSidebarOpen]);

  return (
    <>
      <NewBookContainer>
        <TopContainer open={isOpen}>
          <Line
            icon={<MenuBookIcon fontSize="small" />}
            label="ସମସ୍ତ ପୁସ୍ତକ"
            href="/"
            meta={data.numBooks || '-'}
          />
        </TopContainer>
      </NewBookContainer>
      <Container open={isOpen}>
        <Line
          icon={<CategoryIcon fontSize="small" />}
          label="ବିଷୟ"
          onClick={toggleCategories}
          expandable={true}
          expanded={categoriesExpanded}
          meta={data.numCategories || '-'}
        />
        {categoriesExpanded && (
          <>
            {data.topCategories &&
              data.topCategories.map(
                (
                  {name, bookCount}: {name: string; bookCount: number},
                  idx: number
                ) => (
                  <Line
                    key={name || idx}
                    label={name}
                    meta={bookCount}
                    href={qurl('ବିଷୟ', name)}
                  />
                )
              )}
            <Line
              label="ସମସ୍ତ ବିଷୟ"
              href={qurl('ବିଷୟ', '')}
              icon={<ArrowForwardIcon fontSize="small" />}
            />
          </>
        )}
        <Line
          icon={<FaceIcon fontSize="small" />}
          label="ଲେଖକ"
          onClick={toggleAuthors}
          expandable={true}
          expanded={authorsExpanded}
          meta={data.numAuthors || '-'}
        />
        {authorsExpanded && (
          <>
            {data.topAuthors &&
              data.topAuthors.map(
                (
                  {name, bookCount}: {name: string; bookCount: number},
                  idx: number
                ) => (
                  <Line
                    key={name || idx}
                    label={name}
                    meta={bookCount}
                    href={qurl('ଲେଖକ', name)}
                  />
                )
              )}
            <Line
              label="ସମସ୍ତ ଲେଖକ"
              href={qurl('ଲେଖକ', '')}
              icon={<ArrowForwardIcon fontSize="small" />}
            />
          </>
        )}
        <Line
          icon={<CalendarMonthIcon fontSize="small" />}
          label="ବର୍ଷ"
          onClick={toggleYears}
          expandable={true}
          expanded={yearsExpanded}
          meta={data.numYears || '-'}
        />
        {yearsExpanded && (
          <>
            {data.topYears &&
              data.topYears.map(
                (
                  {year, bookCount}: {year: number; bookCount: number},
                  idx: number
                ) => (
                  <Line
                    key={year || idx}
                    label={year}
                    meta={bookCount}
                    href={qurl('ବର୍ଷ', year.toString())}
                  />
                )
              )}
            <Line
              label="ସମସ୍ତ ବର୍ଷ"
              href={qurl('ବର୍ଷ', '')}
              icon={<ArrowForwardIcon fontSize="small" />}
            />
          </>
        )}
      </Container>
    </>
  );
}
